import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import type { Survey } from 'posthog-js';
import type { ReactElement } from 'react';

import { window } from '../../globals';
import { BrandedLoadingScreen } from '../BrandedLoadingScreen';

import { OnboardingSurveyFullscreen } from './OnboardingSurvey/OnboardingSurveyFullscreen';
import { useSurveyToasts } from './useSurveyToasts';

type Props = {
  children: ReactElement;
};

type GateState =
  | { kind: 'pending' }
  | { kind: 'show'; survey: Survey }
  | { kind: 'pass' };

const TIMEOUT_MS = 3000;

/**
 * Routes active PostHog surveys to the right presentation surface and gates
 * the app shell during the post-signup onboarding survey.
 *
 * The onboarding survey is a blocking takeover — when the current user has
 * `onboardingSurveyCompletedAt == null`, the provider blocks the app shell
 * (rendering a branded loading screen, suppressing all toast survey dispatch)
 * until PostHog responds, then either shows the takeover or unblocks. A 3s
 * timeout fails open so a slow/unreachable PostHog cannot lock users out.
 *
 * The DB column `onboardingSurveyCompletedAt` is the authoritative gate for
 * the takeover: PostHog's `onSurveysLoaded` is used purely as a catalog
 * lookup to obtain the survey definition (questions, appearance) for
 * rendering. PostHog display conditions / targeting flags are intentionally
 * not consulted — they evaluate client-side against state that races with
 * `$identify` propagation (anonymous-then-identified flag re-fetch), which
 * produced misses for users we did want to target (esp. invite-signup
 * sessions, where SurveyProvider mounts before identify settles). Audience
 * gating happens server-side via the column.
 *
 * Non-onboarding surveys (e.g. NPS) render as bottom-right toasts via
 * `addToast({ type: 'survey', ... })`, dispatched once per id per mount, and
 * only after the gate has resolved to `pass`.
 *
 * The transition to `pass` is sticky: once the user has been let through (by
 * completion, mismatch, timeout, or absence of PostHog), no later flag
 * re-evaluation transitions them back to `show`. This prevents the takeover
 * from re-mounting after submission and unmounting the ThanksStep.
 */
export const SurveyProvider = ({ children }: Props) => {
  const posthog = usePostHog();

  const onboardingSurveyId = window.posthogOnboardingSurveyId;
  // The onboarding survey is a post-signup flow keyed to a logged-in user, so
  // bypass the gate entirely for unauthenticated visitors. `SurveyProvider`
  // wraps the whole app shell including public pages (shared report links,
  // login, etc.), and PostHog loads for guests too — without this check those
  // visitors would receive the takeover under a PostHog-generated anonymous
  // distinct ID, producing "Anonymous" responses with no user attribution.
  const isAuthenticated = window.user != null;
  const hasCompletedOnboarding = window.user?.onboardingSurveyCompletedAt != null;

  const initialKind: GateState['kind'] =
    !posthog || !isAuthenticated || hasCompletedOnboarding ? 'pass' : 'pending';
  const [gate, setGate] = useState<GateState>({ kind: initialKind });

  // Resolves the pending gate as soon as PostHog reports its surveys catalog.
  //
  // `onSurveysLoaded` delivers the FULL catalog of configured surveys —
  // unfiltered by display conditions. That's exactly what we want here:
  // audience gating is the DB column's job, not PostHog's. We use the
  // catalog purely to look up the onboarding survey definition (questions,
  // appearance) by id and render the takeover.
  //
  // Why not `getActiveMatchingSurveys` + `onFeatureFlags`? Both depend on
  // PostHog flag evaluation, which races `$identify` propagation:
  // `onFeatureFlags` fires once before identify lands (anonymous-identity
  // flags) and again after, and the survey's auto-created targeting flag is
  // only true on the second firing. Taking the first firing missed every
  // invite-redirect signup; coordinating which firing to trust added
  // brittle "wait for identity" guards that still leaked through the
  // identify-is-client-side-immediate vs server-side-delayed gap. The
  // column sidesteps all of it.
  //
  // `handleSurveys` re-reads `window.user.onboardingSurveyCompletedAt` as
  // defense-in-depth: the DB column is the authoritative completion signal,
  // and a stale `window.user` at first render or a same-session completion
  // from `markLocallyComplete` shouldn't lock us into showing the takeover.
  //
  // `isCancelled` guards against a late PostHog callback writing into an
  // unmounted tree. The 3s timeout fails open to `pass` so a slow or
  // unreachable PostHog cannot lock users out. Re-entry is impossible —
  // once `gate.kind` leaves `pending`, the early return at the top keeps
  // this effect inert for the rest of the component's lifetime.
  useEffect(() => {
    if (gate.kind !== 'pending' || !posthog) {
      return undefined;
    }
    let isCancelled = false;

    const handleSurveys = (surveys: Survey[]) => {
      if (isCancelled) {
        return;
      }
      if (window.user == null || window.user.onboardingSurveyCompletedAt != null) {
        setGate({ kind: 'pass' });
        return;
      }
      const onboardingMatch = onboardingSurveyId
        ? surveys.find((s) => s.id === onboardingSurveyId)
        : undefined;
      setGate(onboardingMatch
        ? { kind: 'show', survey: onboardingMatch }
        : { kind: 'pass' });
    };

    const unsubscribe = posthog.onSurveysLoaded(handleSurveys);
    const timeoutId = window.setTimeout(() => {
      if (!isCancelled) {
        setGate({ kind: 'pass' });
      }
    }, TIMEOUT_MS);

    return () => {
      isCancelled = true;
      unsubscribe();
      window.clearTimeout(timeoutId);
    };
  }, [gate.kind, posthog, onboardingSurveyId]);

  useSurveyToasts({
    enabled: gate.kind === 'pass' && isAuthenticated,
    onboardingSurveyId,
  });

  if (gate.kind === 'pending') {
    return <BrandedLoadingScreen />;
  }
  if (gate.kind === 'show') {
    return (
      <OnboardingSurveyFullscreen
        survey={gate.survey}
        onClose={() => setGate({ kind: 'pass' })}
      />
    );
  }
  return children;
};
